import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
export interface KennisbankArticleProps {
  // Basis
  hero_title: string;
  hero_subtitle: string;
  intro_text: React.ReactNode;
  local_context: React.ReactNode;
  // Voordelen
  benefits_title: string;
  benefits_block: Array<{
    title: string;
    text: string;
  }>;
  // Proces
  process_title: string;
  process_block: Array<{
    title: string;
    text: string;
  }>;
  // Bewijs
  case_section_title: string;
  case_section_text: React.ReactNode;
  // FAQ
  faq_intro: string;
  faq_q1?: string;
  faq_a1?: string;
  faq_q2?: string;
  faq_a2?: string;
  faq_q3?: string;
  faq_a3?: string;
  faq_q4?: string;
  faq_a4?: string;
  faq_q5?: string;
  faq_a5?: string;
  // CTA
  cta_title: string;
  cta_text: string;
  cta_button_text: string;
  cta_button_link: string;
  // SEO
  seo_title: string;
  meta_description: string;
  schema_type: string;
  schema_json: string;
  // Linkblokken
  internal_links_services: Array<{
    label: string;
    url: string;
  }>;
  internal_links_cities: Array<{
    label: string;
    url: string;
  }>;
  related_articles: Array<{
    label: string;
    url: string;
  }>;
  // Extra (handig voor de template weergave)
  breadcrumb_label?: string;
  reading_time?: string;
}
export function KennisbankArticleTemplate({
  hero_title,
  hero_subtitle,
  intro_text,
  local_context,
  benefits_title,
  benefits_block,
  process_title,
  process_block,
  case_section_title,
  case_section_text,
  faq_intro,
  faq_q1,
  faq_a1,
  faq_q2,
  faq_a2,
  faq_q3,
  faq_a3,
  faq_q4,
  faq_a4,
  faq_q5,
  faq_a5,
  cta_title,
  cta_text,
  cta_button_text,
  cta_button_link,
  seo_title,
  meta_description,
  schema_type,
  schema_json,
  internal_links_services,
  internal_links_cities,
  related_articles,
  breadcrumb_label = 'Artikel',
  reading_time = '5 min leestijd'
}: KennisbankArticleProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = seo_title;
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = meta_description;
    // Inject Schema JSON-LD
    if (schema_json) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = schema_json;
      script.id = 'article-schema';
      document.head.appendChild(script);
      return () => {
        const el = document.getElementById('article-schema');
        if (el) el.remove();
      };
    }
  }, [seo_title, meta_description, schema_json]);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  // Verzamel FAQ's dynamisch
  const faqs = [];
  if (faq_q1 && faq_a1) faqs.push({
    q: faq_q1,
    a: faq_a1
  });
  if (faq_q2 && faq_a2) faqs.push({
    q: faq_q2,
    a: faq_a2
  });
  if (faq_q3 && faq_a3) faqs.push({
    q: faq_q3,
    a: faq_a3
  });
  if (faq_q4 && faq_a4) faqs.push({
    q: faq_q4,
    a: faq_a4
  });
  if (faq_q5 && faq_a5) faqs.push({
    q: faq_q5,
    a: faq_a5
  });
  return <main className="pt-16 lg:pt-[72px]" data-id="element-634">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-635">
        <div className="absolute inset-0 pointer-events-none" data-id="element-636">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-637" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-638" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-639" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-640">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-641">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-642">
              Home
            </a>
            <span className="text-navy-500" data-id="element-643">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-644">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-645">/</span>
            <span className="text-accent-light font-medium" data-id="element-646">
              {breadcrumb_label}
            </span>
          </nav>

          {/* ACF: hero_title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-647">
            {hero_title}
          </h1>

          {/* ACF: hero_subtitle */}
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-2" data-id="element-648">
            {hero_subtitle}
          </p>
        </div>
      </section>

      {/* Main Content Area - Single Column */}
      <div className="bg-white py-16 lg:py-24" data-id="element-649">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-650">
          {/* Intro & Local Context */}
          <div className="prose prose-lg prose-slate max-w-none mb-16" data-id="element-651">
            {intro_text}

            <h2 className="text-2xl font-bold text-navy-900 mt-12 mb-4" data-id="element-652">
              Lokale impact
            </h2>
            <div className="text-slate-700 leading-relaxed" data-id="element-653">
              {local_context}
            </div>
          </div>

          {/* Voordelen (benefits_block) */}
          <div className="mb-16" data-id="element-654">
            <h2 className="text-3xl font-bold text-navy-900 mb-8" data-id="element-655">
              {benefits_title}
            </h2>
            <div className="space-y-8" data-id="element-656">
              {benefits_block.map((benefit, index) => <div key={index} data-id="element-657">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-658">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-700 leading-relaxed text-lg" data-id="element-659">
                    {benefit.text}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Proces (process_block) */}
          <div className="mb-16" data-id="element-660">
            <h2 className="text-3xl font-bold text-navy-900 mb-8" data-id="element-661">
              {process_title}
            </h2>
            <div className="space-y-8" data-id="element-662">
              {process_block.map((step, index) => <div key={index} data-id="element-663">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-664">
                    {index + 1}. {step.title}
                  </h3>
                  <p className="text-slate-700 leading-relaxed text-lg" data-id="element-665">
                    {step.text}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Bewijs / Case Section */}
          <div className="mb-16" data-id="element-666">
            <h2 className="text-3xl font-bold text-navy-900 mb-6" data-id="element-667">
              {case_section_title}
            </h2>
            <div className="prose prose-lg prose-slate max-w-none" data-id="element-668">
              {case_section_text}
            </div>
          </div>

          {/* FAQ Section */}
          {faqs.length > 0 && <div className="mb-16" data-id="element-669">
              <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-670">
                Veelgestelde vragen
              </h2>
              <p className="text-lg text-slate-700 mb-8" data-id="element-671">{faq_intro}</p>

              <div className="space-y-4" data-id="element-672">
                {faqs.map((faq, index) => <div key={index} className="border-b border-navy-100 last:border-0" data-id="element-673">
                    <button onClick={() => toggleFaq(index)} className="w-full py-5 flex items-center justify-between text-left focus:outline-none group" data-id="element-674">
                      <span className="font-bold text-navy-900 text-lg group-hover:text-accent transition-colors" data-id="element-675">
                        {faq.q}
                      </span>
                      {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0 ml-4" data-id="element-676" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0 ml-4 group-hover:text-accent transition-colors" data-id="element-677" />}
                    </button>
                    {openFaq === index && <div className="pb-5 text-slate-700 leading-relaxed text-lg" data-id="element-678">
                        {faq.a}
                      </div>}
                  </div>)}
              </div>
            </div>}
        </div>
      </div>

      {/* Linkblokken */}
      <section className="py-16 bg-surface-muted border-t border-navy-100" data-id="element-679">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-680">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-681">
            {/* ACF: internal_links_services */}
            <div data-id="element-682">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-683">
                Gerelateerde diensten
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-684">
                {internal_links_services.map((link, i) => <a key={i} href={link.url} className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-685">
                    {link.label}
                  </a>)}
              </nav>
            </div>

            {/* ACF: internal_links_cities */}
            <div data-id="element-686">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-687">
                Lokaal webdesign
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-688">
                {internal_links_cities.map((link, i) => <a key={i} href={link.url} className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-689">
                    {link.label}
                  </a>)}
              </nav>
            </div>

            {/* ACF: related_articles */}
            <div data-id="element-690">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-691">
                Meer lezen
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-692">
                {related_articles.map((link, i) => <a key={i} href={link.url} className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-693">
                    {link.label}
                  </a>)}
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Custom CTA (overrides FinalCtaSection for this template) */}
      <section className="py-24 bg-accent text-white text-center px-4" data-id="element-694">
        <div className="max-w-3xl mx-auto" data-id="element-695">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-696">{cta_title}</h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-697">{cta_text}</p>
          <a href={cta_button_link} className="inline-flex items-center gap-2 bg-white text-accent hover:bg-navy-50 font-bold px-8 py-4 rounded-button shadow-lg transition-all duration-200 text-lg" data-id="element-698">
            {cta_button_text}
            <ArrowRightIcon className="w-5 h-5" data-id="element-699" />
          </a>
        </div>
      </section>
    </main>;
}